import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import projectMilestonesListApi from '../../../middleware/api/Invoicing/ProjectMilestones/projectMilestoneApi'
import {
  BillingReportList,
  GetClients,
  MilestoneReportQueryParams,
  ProjectMilestoneListState,
  ProjectMilestoneQueryParams,
} from '../../../types/Invoicing/ProjectMilestones/projectMilestonetype'
import { RootState } from '../../../stateStore'
import { ApiLoadingState } from '../../../middleware/api/apiList'

const getBillingReports = createAsyncThunk(
  'clientList/getBillingReports',
  async (queryParams: MilestoneReportQueryParams, thunkApi) => {
    try {
      return await projectMilestonesListApi.billingReport(queryParams)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getBillingReportSearch = createAsyncThunk(
  'clientList/getBillingReportSearch',
  async (queryParams: ProjectMilestoneQueryParams, thunkApi) => {
    try {
      return await projectMilestonesListApi.getBillingReportSearch(queryParams)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const reOpenMilestoneList = createAsyncThunk(
  'clientList/getBillingReports',
  async (milestoneId: number, thunkApi) => {
    try {
      return await projectMilestonesListApi.reOpenMilestone(milestoneId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const status = 'TRUE'

export const initialMilestoneListate: ProjectMilestoneListState = {
  getClients: [],
  isLoading: ApiLoadingState.idle,
  reports: [],
  SelectValue: '',
  statusValue: status,
  clientValue: '',
  searchstr: '',
  customFromValue: '',
  customToValue: '',
  listSize: 0,
  milestoneId: 0,
  billingReportList: {} as BillingReportList,
  getClientsDetailsList: '',
  searchOn: false,
  isRaisedOnMilestone: false,
  isSearch: false, // a separate flag from searchOn since the former is kinda autistic
}
const projectMilestoneSlice = createSlice({
  name: 'clientList',
  initialState: initialMilestoneListate,
  reducers: {
    setSelectValue: (state, action) => {
      state.SelectValue = action.payload
    },
    setStatusValue: (state, action) => {
      state.statusValue = action.payload
    },
    setClientValue: (state, action) => {
      state.clientValue = action.payload
    },
    setSearchValue: (state, action) => {
      state.searchstr = action.payload
    },
    setCustomFromValue: (state, action) => {
      state.customFromValue = action.payload
    },
    setCustomToValue: (state, action) => {
      state.customToValue = action.payload
    },
    setIsRaisedOnMilestone: (state, action) => {
      state.isRaisedOnMilestone = action.payload
    },
    setSearchOn: (state, action) => {
      state.searchOn = action.payload
    },
    setIsSearch: (state, action) => {
      state.isSearch = action.payload
    },
  },

  extraReducers: (builder) => {
    builder
      .addMatcher(isAnyOf(getBillingReports.pending), (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addMatcher(isAnyOf(getBillingReportSearch.pending), (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addMatcher(isAnyOf(getBillingReports.fulfilled), (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.billingReportList = action.payload
        state.searchOn = false
      })
      .addMatcher(
        isAnyOf(getBillingReportSearch.fulfilled),
        (state, action) => {
          state.isLoading = ApiLoadingState.succeeded
          state.billingReportList = action.payload
          state.searchOn = true
        },
      )
  },
})
const projectMilestoneListThunk = {
  getBillingReports,
  reOpenMilestoneList,
  getBillingReportSearch,
}
const isLoading = (state: RootState): LoadingState =>
  state.projectMilestonesReport.isLoading

const getClientsDetailsList = (state: RootState): GetClients[] =>
  state.projectMilestonesReport.getClients

const billingReporList = (state: RootState): BillingReportList =>
  state.projectMilestonesReport.billingReportList

const SelectValue = (state: RootState): string =>
  state.projectMilestonesReport.SelectValue

const StatusValue = (state: RootState): string =>
  state.projectMilestonesReport.statusValue

const searchValue = (state: RootState): string =>
  state.projectMilestonesReport.searchstr

const clientValue = (state: RootState): string =>
  state.projectMilestonesReport.clientValue

const customFromValue = (state: RootState): string | Date =>
  state.projectMilestonesReport.customFromValue

const customToValue = (state: RootState): string | Date =>
  state.projectMilestonesReport.customToValue

const reOpenMilestoneValue = (state: RootState): number =>
  state.projectMilestonesReport.milestoneId

const listSize = (state: RootState): number =>
  state.projectMilestonesReport.billingReportList?.size

const getIsSearchOn = (state: RootState): boolean =>
  state.projectMilestonesReport.searchOn

const getIsRaisedOnMilestone = (state: RootState): boolean =>
  state.projectMilestonesReport.isRaisedOnMilestone

const getIsSearch = (state: RootState): boolean =>
  state.projectMilestonesReport.isSearch

const projectMilestonesListSelectors = {
  isLoading,
  getClientsDetailsList,
  billingReporList,
  customToValue,
  SelectValue,
  listSize,
  searchValue,
  clientValue,
  customFromValue,
  reOpenMilestoneValue,
  StatusValue,
  getIsSearchOn,
  getIsRaisedOnMilestone,
  getIsSearch,
}
export const projectMilestonesListService = {
  ...projectMilestoneListThunk,
  actions: projectMilestoneSlice.actions,
  selectors: projectMilestonesListSelectors,
}

export default projectMilestoneSlice.reducer
